import React, { useEffect, useMemo, useState } from "react";
import {
  connectHostedBlog,
  disconnectHostedBlog,
  getHostedBlog,
  isHostedPending,
  type HostedBlog,
} from "../services/blogPublishingService";

/**
 * Connect a blog for a site builder with no blog API (Squarespace, Wix, GoDaddy…):
 * Sagelyn hosts the blog at blog.<their domain>, which takes ONE DNS record.
 * Used in the blog editor panel (when nothing is connected), on Connections, and
 * in chat. Polls while waiting, so it turns green by itself once DNS is in place.
 */

interface ConnectBlogCardProps {
  customerId: string;
  idToken: string;
  /** The Brand's name, used as the blog's site title. */
  businessName?: string;
  /** The Brand's main website, used to suggest blog.<domain> and link back. */
  websiteUrl?: string;
  /** Which builder the owner said they use; only changes the help wording. */
  platform?: "squarespace" | "wix" | "other";
  /** Already-loaded state (avoids a second fetch when the parent has it). */
  initial?: HostedBlog | null;
  onChange?: (hosted: HostedBlog) => void;
}

const POLL_MS = 15000;

/**
 * Where the record goes and what each field is called, in the builder's own
 * words and in the order its form shows them. Squarespace's form (checked
 * 2026-10-14) is TYPE, NAME, PRIORITY, TTL, DATA; "Host" and "Value" made the
 * owner translate.
 */
type FieldLabels = { type: string; name: string; value: string; extra?: string };
const PLATFORM_STEPS: Record<string, { name: string; where: string; labels: FieldLabels }> = {
  squarespace: {
    name: "Squarespace",
    where: "Settings › Domains › your domain › DNS › Custom records › Add record",
    labels: { type: "Type", name: "Name", value: "Data", extra: "Leave Priority empty and TTL as it is, then click Save." },
  },
  wix: {
    name: "Wix",
    where: "Domains › your domain › Manage DNS records › CNAME › Add record",
    labels: { type: "Type", name: "Host name", value: "Value", extra: "Leave TTL as it is, then click Save." },
  },
  other: {
    name: "your domain provider",
    where: "the DNS settings for your domain (often called DNS records or Advanced DNS)",
    labels: { type: "Type", name: "Host / Name", value: "Value / Data / Points to" },
  },
};

const domainOf = (url?: string): string => {
  try {
    return url ? new URL(url.startsWith("http") ? url : `https://${url}`).hostname.replace(/^www\./, "") : "";
  } catch {
    return "";
  }
};

export const ConnectBlogCard: React.FC<ConnectBlogCardProps> = ({
  customerId,
  idToken,
  businessName = "",
  websiteUrl = "",
  platform = "squarespace",
  initial,
  onChange,
}) => {
  const domain = domainOf(websiteUrl);
  const [hosted, setHosted] = useState<HostedBlog | null>(initial ?? null);
  const [loading, setLoading] = useState(!initial);
  const [hostname, setHostname] = useState(domain ? `blog.${domain}` : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const update = (h: HostedBlog) => {
    setHosted(h);
    onChange?.(h);
  };

  useEffect(() => {
    if (initial) return;
    let active = true;
    getHostedBlog(customerId, idToken)
      .then((h) => active && update(h))
      .catch((e: unknown) => active && setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, idToken]);

  // While waiting for DNS, check again every 15s: the card turns green on its own.
  const pending = isHostedPending(hosted);
  useEffect(() => {
    if (!pending) return;
    const id = window.setInterval(() => {
      getHostedBlog(customerId, idToken).then(update).catch(() => undefined);
    }, POLL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, customerId, idToken]);

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      update(
        await connectHostedBlog(customerId, idToken, {
          hostname,
          siteTitle: businessName || domain || hostname,
          homeUrl: websiteUrl || (domain ? `https://${domain}` : ""),
        })
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to connect");
    } finally {
      setBusy(false);
    }
  };

  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const startOver = async () => {
    setBusy(true);
    setError(null);
    try {
      await disconnectHostedBlog(customerId, idToken);
      setConfirmingDisconnect(false);
      update({ connected: false });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to reset");
    } finally {
      setBusy(false);
    }
  };

  const steps = PLATFORM_STEPS[platform] ?? PLATFORM_STEPS.other;
  const record = hosted?.dnsRecord;
  const emailHref = useMemo(() => {
    if (!record) return "";
    const body =
      `Hi,\n\nCould you add this DNS record for ${domainOf(hosted?.hostname) || "our domain"}? ` +
      `It puts our blog at ${hosted?.hostname}.\n\n` +
      `${steps.labels.type}: ${record.type}\n${steps.labels.name}: ${record.name}\n${steps.labels.value}: ${record.value}\n` +
      (steps.labels.extra ? `${steps.labels.extra}\n\n` : "\n") +
      `Where: ${steps.where}\n\n` +
      // Without a menu link the blog is an orphan nobody finds (SGL-500); whoever
      // adds the record usually manages the site's menu too.
      `Once the blog is live, please also add a "Blog" link to our website's menu pointing to ` +
      `https://${hosted?.hostname}${steps.name === "Squarespace" ? " (in Squarespace: Pages › + › Link)" : ""}.\n\n` +
      `Thank you!`;
    return `mailto:?subject=${encodeURIComponent("Please add one DNS record for our blog")}&body=${encodeURIComponent(body)}`;
  }, [record, hosted?.hostname, steps]);

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      setCopied(null);
    }
  };

  if (loading) {
    return <div className="rounded-xl border border-theme-border p-4 text-sm text-theme-fg-muted">Checking your blog connection…</div>;
  }

  if (hosted?.connected && hosted.status === "CONNECTED") {
    return (
      <div className="rounded-xl border border-theme-border bg-theme-surface p-3">
        <div className="flex items-center gap-3">
          <span className="material-icons text-[20px] text-theme-primary">check_circle</span>
          <div className="min-w-0 flex-1 text-sm">
            <span className="font-semibold text-theme-fg">Blog connected</span>
            <span className="text-theme-fg-muted"> · {hosted.hostname}</span>
          </div>
          <a href={`https://${hosted.hostname}/`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-theme-primary">
            View blog
          </a>
          {!confirmingDisconnect && (
            <button type="button" onClick={() => setConfirmingDisconnect(true)} className="text-xs font-semibold text-theme-fg-muted hover:text-red-700">
              Disconnect
            </button>
          )}
        </div>
        <p className="mt-2 text-[11px] text-theme-fg-muted">
          So visitors can find it, add a <span className="font-medium text-theme-fg">Blog</span> link to your website's menu
          pointing to https://{hosted.hostname}
          {steps.name === "Squarespace" ? " (in Squarespace: Pages › + › Link)" : ""}.
        </p>
        {confirmingDisconnect && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800" role="alert">
            <p>
              Your blog stops showing at {hosted.hostname}. Your posts are kept, and come back if you connect again,
              at this address or a new one. You can also delete the DNS record in {steps.name}.
            </p>
            {error && <p className="mt-2 font-semibold">{error}</p>}
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={startOver} disabled={busy} className="rounded-md bg-red-700 px-3 py-1.5 font-semibold text-white disabled:opacity-50">
                {busy ? "Disconnecting…" : "Disconnect blog"}
              </button>
              <button type="button" onClick={() => setConfirmingDisconnect(false)} disabled={busy} className="rounded-md px-3 py-1.5 font-semibold text-red-800">
                Keep it
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <section aria-label="Connect your blog" className="rounded-xl border border-theme-border bg-theme-surface p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-theme-fg">Connect your blog</h3>
        {pending && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-theme-primary/10 px-2.5 py-1 text-[11px] font-semibold text-theme-primary">
            <span className="h-2 w-2 animate-pulse rounded-full bg-theme-primary" />
            Waiting for the DNS record
          </span>
        )}
      </div>

      {error && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

      {!hosted?.connected ? (
        <>
          <p className="mb-3 text-xs leading-relaxed text-theme-fg-muted">
            {steps.name === "your domain provider" ? "Your site builder" : steps.name} doesn't let other apps post to its blog, so
            Sagelyn hosts your blog on your own domain. Your main site stays where it is.
          </p>
          <label htmlFor="blog-hostname" className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            Blog address
          </label>
          <div className="flex gap-2">
            <input
              id="blog-hostname"
              type="text"
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              placeholder="blog.yourdomain.com"
              className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-theme-primary focus:ring-1 focus:ring-theme-primary"
            />
            <button
              type="button"
              onClick={connect}
              disabled={busy || !hostname.trim()}
              className="shrink-0 rounded-lg bg-theme-primary px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Connecting…" : "Continue"}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="mb-2 text-xs text-theme-fg-muted">
            Add this record in {steps.name}: <span className="font-medium text-theme-fg">{steps.where}</span>
          </p>
          {record && (
            <ol className="flex flex-col gap-2 rounded-lg border border-theme-border p-3 text-xs" aria-label="DNS record to add">
              {[
                { label: steps.labels.type, value: record.type, hint: "Choose it from the list" },
                { label: steps.labels.name, value: record.name, copyable: true },
                { label: steps.labels.value, value: record.value, copyable: true },
              ].map((f) => (
                <li key={f.label} className="flex items-center gap-3">
                  <span className="w-20 shrink-0 font-semibold uppercase tracking-wider text-gray-500">{f.label}</span>
                  <span className="min-w-0 flex-1 truncate font-mono text-theme-fg">{f.value}</span>
                  {f.copyable ? (
                    <button
                      type="button"
                      onClick={() => copyText(f.value, f.label)}
                      className="shrink-0 rounded-md border border-theme-border bg-white px-2 py-1 text-[11px] font-semibold text-theme-primary"
                    >
                      {copied === f.label ? "Copied" : "Copy"}
                    </button>
                  ) : (
                    <span className="shrink-0 text-[11px] text-theme-fg-muted">{f.hint}</span>
                  )}
                </li>
              ))}
              {steps.labels.extra && <li className="pt-1 text-theme-fg-muted">{steps.labels.extra}</li>}
            </ol>
          )}
          {hosted.dns?.message && (
            <p className={`mt-2 text-xs ${hosted.dns.ok ? "text-theme-fg-muted" : "text-theme-primary"}`}>{hosted.dns.message}</p>
          )}
          {hosted.dns?.ok && hosted.waitReason === "previous_connection" && (
            <p className="mt-1 text-xs text-theme-fg-muted">
              Your earlier connection to this address is still being removed. This finishes on its own, usually within 15 minutes.
            </p>
          )}
          {hosted.dns?.ok && hosted.waitReason !== "previous_connection" && (
            <p className="mt-1 text-xs text-theme-fg-muted">Now securing your address (https). This usually takes 5 to 30 minutes.</p>
          )}
          {hosted.status === "FAILED" && (
            <p className="mt-2 text-xs text-red-700">{hosted.error || "We couldn't finish connecting this address."}</p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {emailHref && (
              <a href={emailHref} className="rounded-lg border border-theme-border bg-white px-3 py-2 text-xs font-semibold text-theme-primary">
                Email these steps to my web person
              </a>
            )}
            <button type="button" onClick={startOver} disabled={busy} className="rounded-lg px-3 py-2 text-xs font-semibold text-theme-fg-muted hover:bg-gray-50">
              Use a different address
            </button>
          </div>
          <p className="mt-3 border-t border-theme-border pt-3 text-[11px] text-theme-fg-muted">
            We check automatically. This turns green on its own once the record is in place, usually within minutes.
          </p>
        </>
      )}
    </section>
  );
};

export default ConnectBlogCard;
